import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import { Visibility, VisibilityOff, Refresh } from '@mui/icons-material';
import { setToken, setUser } from '../../store/slices/authSlice';
import { authService } from '../../services/authService';
import './style.css';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [showClearSession, setShowClearSession] = useState(false);
  const [formData, setFormData] = useState({
    phone: '',
    password: '',
  });

  // Check for stale authentication data on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const authError = localStorage.getItem('auth_error');
    
    if (token || authError) {
      setShowClearSession(true);
      if (authError) {
        setError('Your previous session has expired. Please log in again.');
        localStorage.removeItem('auth_error');
      }
    }
  }, []);

  const validatePhone = (phone: string): boolean => {
    // Remove any non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length === 10;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Only allow digits
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length > 10) return; // Don't allow more than 10 digits
      
      setFormData(prev => ({
        ...prev,
        [name]: digitsOnly
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    setError('');
    setInfo('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!validatePhone(formData.phone)) {
      setError('Please enter a valid 10-digit phone number');
      setLoading(false);
      return;
    }

    try {
      const response = await authService.login({
        phoneNumber: `+91${formData.phone}`,
        securityPin: formData.password
      });

      if (response.success && response.data.access_token) {
        // Store token and user data
        dispatch(setToken(response.data.access_token));
        dispatch(setUser({
          userName: response.data.userName,
          phone: response.data.phoneNumber,
          fullName: response.data.fullName,
          id: response.data.id,
          role: response.data.userRole
        }));
        
        // Clear any previous session data
        setShowClearSession(false);
        navigate('/select-student');
      } else {
        setError('Login failed. Please try again.');
      }
    } catch (err) {
      setError('Invalid phone number or security pin');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    // Self-service PIN reset needs an SMS/OTP flow that doesn't exist yet. Until then,
    // give a real answer instead of a dead button: PINs are managed by the school.
    setError('');
    setInfo('To reset your Security PIN, please contact your school administrator.');
  };

  const handleClearSession = () => {
    // Clear all authentication data comprehensively for student privacy
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    localStorage.removeItem('auth_error');
    localStorage.removeItem('lastStartedTest');
    localStorage.removeItem('lastTestStartTime');
    localStorage.removeItem('selectedTest');
    localStorage.removeItem('persist:root');
    sessionStorage.clear();
    
    // Reset form
    setFormData({ phone: '', password: '' });
    setError('');
    setShowClearSession(false);
    
    // Reload page to reset any cached state
    window.location.reload();
  };

  return (
    <div className="login-card">
      <div className="login-brand">
        <img src="/lo_icon.svg" alt="NextZenOlympiad" className="login-brand-icon" />
        <span className="login-brand-name">Live<span>Olympiad</span></span>
      </div>
      <div className="login-title">Welcome back</div>
      <div className="login-subtitle">Sign in with your phone number to continue your practice.</div>
      
      {/* Clear Session Alert */}
      {showClearSession && (
        <Alert 
          severity="info" 
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleClearSession}
              startIcon={<Refresh />}
              sx={{ color: 'inherit', fontWeight: 'bold' }}
            >
              Clear Session
            </Button>
          }
          sx={{ mb: 2 }}
        >
          Previous session detected. Click "Clear Session" if you're having trouble logging in.
        </Alert>
      )}
      
      {/* Login errors belong to the whole form, not the phone field — a wrong PIN
          previously showed its error under "Phone Number", which misled users. */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {info}
        </Alert>
      )}

      {/* autoComplete was "off", which is why saved credentials never filled. On a family
          device the same parent signs several children in; making them retype a phone and
          PIN each time is friction with no security benefit, since the credentials are
          already stored in the browser either way. */}
      <form className="login-form" onSubmit={handleSubmit} autoComplete="on">
        <TextField
          fullWidth
          name="phone"
          label="Phone Number"
          value={formData.phone}
          onChange={handleChange}
          variant="outlined"
          className="login-input"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <span role="img" aria-label="India flag" style={{ fontSize: 22 }}>🇮🇳</span>
              </InputAdornment>
            ),
            style: { background: '#f7f8f9', borderRadius: 10 }
          }}
          required
          inputProps={{ 
            maxLength: 10, 
            style: { fontFamily: 'Montserrat', fontSize: 18 },
            pattern: '[0-9]*',
            inputMode: 'numeric',
            // 'tel' rather than 'username': the field is a phone number, and telling the
            // password manager what it actually is makes it offer the right value.
            autoComplete: 'tel',
            'aria-label': 'Phone number',
          }}
        />
        
        <TextField
          fullWidth
          name="password"
          label="Security PIN"
          required
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange}
          variant="outlined"
          className="login-input"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
            style: { background: '#f7f8f9', borderRadius: 10 }
          }}
          inputProps={{ 
            style: { fontFamily: 'Montserrat', fontSize: 18 },
            pattern: '[0-9]*',
            inputMode: 'numeric'
          ,
            // Lets a password manager offer the stored PIN for this account.
            autoComplete: 'current-password',
            'aria-label': 'Security PIN'
          }}
        />
        
        <button
          type="button"
          className="forgot-password"
          onClick={handleForgotPassword}
        >
          Forgot Security PIN?
        </button>
        
        <Button
          type="submit"
          variant="contained"
          fullWidth
          className="login-button"
          disabled={loading}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} sx={{ color: 'white' }} />
              Logging In...
            </Box>
          ) : (
            'Log In'
          )}
        </Button>
      </form>
    </div>
  );
};

export default Login;